import { useAuthStore } from "@/store/authStore";

export type Language = "en" | "de";

const LANGUAGE_STORAGE_KEY = "preferred_language";

const translations = {
  en: {
    appTitle: "Bundestippliga",
    menu: "Menu",
    home: "Home",
    matches: "Matches",
    predictions: "Predictions",
    teams: "Teams",
    leaderboard: "Rankings",
    profile: "Profile",
    admin: "Admin",
    logout: "Logout",
    allLeagues: "All Leagues",
    more: "More",
    username: "Username",
    landingWelcomeTitle: "Welcome to Bundestippliga",
    landingWelcomeBody:
      "Predict every matchday, climb the leagues, and compete with friends in the Bundesliga prediction league.",
    landingHowItWorks: "How it works",
    landingHowItWorksItem1: "Pick scores for each matchday.",
    landingHowItWorksItem2:
      "Earn points for correct results and exact scores.",
    landingHowItWorksItem3: "Rise through the leagues every round.",
    loginTitle: "Login",
    loginButton: "Login",
    loginUsernameLabel: "Username or Email",
    loginPasswordLabel: "Password",
    loginLoading: "Logging in...",
    loginNoAccount: "Don't have an account? Register",
    registerTitle: "Register",
    registerCardTitle: "Create Account",
    registerUsernameLabel: "Username",
    registerEmailLabel: "Email",
    registerPasswordLabel: "Password",
    registerConfirmPasswordLabel: "Confirm Password",
    registerButton: "Register",
    registerLoading: "Creating account...",
    registerHaveAccount: "Already have an account? Login",
    preferredLanguage: "Preferred Language",
    english: "English",
    german: "Deutsch",
    profileTitle: "Profile",
    playerInformation: "Player Information",
    displayName: "Display Name",
    email: "Email",
    totalPoints: "Total Points",
    points: "points",
    statistics: "Statistics",
    accountActions: "Account Actions",
    languagePreferences: "Language Preferences",
    saveLanguage: "Save Language",
    savingLanguage: "Saving language...",
    languageSaved: "Language preference saved.",
    welcomeUser: "Welcome",
    homeIntro: "Predict Bundesliga matches and compete with other players!",
    quickStats: "Quick Stats",
    currentStatus: "Current Status",
    seasonStatus: "Season Status",
    nextMatchday: "Next Matchday",
    matchdayInfoLoading: "Matchday information will be loaded",
    active: "Active",
    completed: "Completed",
    upcoming: "Upcoming",
    viewUpcomingMatches: "View upcoming matches",
    seeLeaderboard: "See leaderboard",
    teamInformation: "Team information",
    makeYourPicks: "Make your picks",
    matchesPageTitle: "Bundesliga Matches",
    matchdaysSegment: "Matchdays",
    upcomingSegment: "Upcoming",
    allMatchesSegment: "All Matches",
    loadingMatches: "Loading matches...",
    joinNewSeason: "Join the new season",
    joinNewSeasonBody:
      "Take a bot's place in the highest league that still has bots. Your previous season result stays in History.",
    joinSeasonButton: "Join season",
    joiningSeason: "Joining season...",
    joinSeasonSuccess: "You're in! Assigned to",
    joinSeasonError: "Could not join the season. Please try again.",
    alreadyInSeason: "You have already joined this season.",
    matchdaysCardTitle: "Bundesliga Matchdays",
    matchdaysCardIntro:
      "Click on a matchday to make predictions for all matches in that round:",
    matchdayLabel: "Matchday",
    sampleDataNote: "Note: Showing sample data. API is currently unavailable.",
    matchInfo: "Match Info",
    seasonLabel: "Season",
    yourPrediction: "Your Prediction",
    score: "Score",
    makePredictionsForMatchday: "Make Predictions for Matchday",
    noUpcomingMatches: "No upcoming matches found.",
    noMatches: "No matches found.",
    predictionNone: "No Prediction",
    predictionPerfect: "Perfect!",
    predictionCorrect: "Correct Result",
    predictionWrong: "Wrong",
    predictionPredicted: "Predicted",
    invalidDate: "Invalid date",
    errorTitle: "Error",
    successTitle: "Success",
    okButton: "OK",
    passwordsDoNotMatch: "Passwords do not match",
    leaderboardTitle: "Leaderboard",
    myLeague: "My League",
    overall: "Overall",
    overallRankings: "Overall Rankings",
    loadingLeaderboard: "Loading leaderboard...",
    youTag: " (You)",
    predictionsCount: "predictions",
    exactScoresLabel: "exact scores",
    tierLabel: "Tier",
    divisionLabel: "Division",
    playersLabel: "players",
    yourPosition: "Your Position",
    loadingLeagueStandings: "Loading league standings...",
    leagueStandingsTitle: "League Standings",
    notInLeague: "You are not currently in a league.",
    joinLeague: "Join a League",
    promotionRelegation: "Promotion & Relegation",
    promotionZone: "Promotion Zone",
    promotionZoneDesc: "Top 3 players advance to higher tier",
    safeZone: "Safe Zone",
    safeZoneDesc: "Remain in current tier",
    relegationZone: "Relegation Zone",
    relegationZoneDesc: "Bottom 3 players drop to lower tier",
    exactShort: "exact",
    goalDiffShort: "goal diff",
    tendencyShort: "tendency",
    allLeaguesTitle: "All Leagues",
    loadingLeagues: "Loading leagues...",
    errorLoadingLeagues: "Error loading leagues. Please try again.",
    browseAllLeagues: "Browse All Leagues",
    exploreAllLeagues: "Explore all",
    leaguesInSystem: "leagues in the system",
    roundLabel: "Round",
    matchdaysLabel: "Matchdays",
    currentLabel: "Current",
    membersLabel: "members",
    allPlayersLabel: "All",
    noPlayersInLeague: "No players in this league yet.",
    noLeaguesFound: "No leagues found.",
    collapseAll: "Collapse All",
    expandAll: "Expand All",
    matchdayTitle: "Matchday",
    loadingMatchday: "Loading matchday...",
    loadingMatchdayData: "Loading matchday data...",
    errorLoadingMatchday: "Failed to load matchday data. Please try again.",
    noDataReceived: "No data received",
    makePredictions: "Make Predictions",
    viewResults: "View Results",
    matchdayStatistics: "Matchday Statistics",
    predictorsLabel: "Predictors",
    finishedLabel: "Finished",
    finalScore: "Final Score",
    predictionLocked: "Prediction Locked",
    noPredictionMade: "No prediction made",
    yourPredictionResult: "Your Prediction & Result",
    predictedLabel: "Predicted",
    saveAllPredictions: "Save All Predictions",
    saving: "Saving...",
    predictionsSaved: "Predictions saved successfully!",
    enterAtLeastOnePrediction: "Please enter at least one prediction",
    failedToSavePredictions: "Failed to save predictions",
    exactScoreLabel: "Exact Score",
    goalDifferenceLabel: "Goal Difference",
    tendencyLabel: "Tendency",
    noPointsLabel: "No Points",
    adminDashboardTitle: "Admin Dashboard",
    manualCronExecution: "Manual Cron Job Execution",
    manualCronDescription:
      "Manually trigger scheduled tasks. Use with caution as some jobs may take several minutes to complete.",
    cronJobStatus: "Cron Job Status",
    refresh: "Refresh",
    autoRefreshEvery30: "Auto-refreshes every 30 seconds",
    manualTriggers: "Manual Triggers",
    calculateScores: "Calculate Scores",
    updateFixtures: "Update Fixtures",
    updateStandings: "Update Standings",
    processPromotionRelegation: "Process Promotion/Relegation",
    endRoundProcessPR: "End Round & Process P/R",
    stopAllCronJobs: "Stop All Cron Jobs",
    cronJobResult: "Cron Job Result",
    rateLimitExceeded:
      "Rate limit exceeded. Please wait a moment before trying again.",
    errorPrefix: "Error",
    lastRun: "Last run",
    nextRun: "Next run",
    never: "Never",
    systemInformation: "System Information",
    environment: "Environment",
    lastStatusUpdate: "Last Status Update",
    autoRefresh: "Auto-refresh",
    every5Seconds: "Every 5 seconds",
    cronDescriptionScore: "Calculate scores for completed matches",
    cronDescriptionFixture: "Update fixtures from external API",
    cronDescriptionStandings: "Update league standings and rankings",
    cronDescriptionPromotion: "Process promotion and relegation",
    cronDescriptionRoundTransition: "End round and process promotion/relegation",
    predictionsTitle: "My Predictions",
    loadingPredictions: "Loading predictions...",
    errorPredictions: "Error loading predictions. Please try again.",
    actualResult: "Actual Result",
    status: "Status",
    pointsEarned: "Points Earned",
    matchDate: "Match Date",
    predictionMade: "Prediction Made",
    noPredictions: "No predictions found. Start predicting matches!",
    viewMatches: "View Matches",
    pending: "Pending",
    teamsTitle: "Teams",
    searchTeamsPlaceholder: "Search teams...",
    loadingTeams: "Loading teams...",
    errorTeams: "Error loading teams. Please try again.",
    shortName: "Short Name",
    leagueLabel: "League",
    noTeams: "No teams found.",
    historyTitle: "History",
    loadingHistory: "Loading history...",
    errorLoadingHistory: "Error loading history. Please try again.",
    pastSeasons: "Past Seasons",
    noPastSeasons: "No completed seasons yet.",
    roundsPlayed: "rounds played",
    finalStandings: "Final Standings",
    finalRound: "Final Round",
    howItWorksNav: "How it works",
    howItWorksTitle: "Play. Climb. Own the Spieltag.",
    howItWorksLead:
      "Bundestippliga is a free Bundesliga prediction league. Tip every match, collect points, and fight for promotion through the Tippligen — just like the real table, only you are the team.",
    howItWorksCtaRegister: "Create a free account",
    howItWorksCtaLogin: "I already have an account",
    howItWorksPlayTitle: "Tip every matchday",
    howItWorksPlayBody:
      "Each Spieltag has nine Bundesliga matches. Enter the exact score you expect, then save. Empty fields count as 0–0, so fill them in with intent. You can change a tip until that match locks.",
    howItWorksDeadlineTitle: "Locks one minute before kickoff",
    howItWorksDeadlineBody:
      "Every match has its own deadline: one minute before kickoff. Friday night's game can already be locked while Sunday's matches are still open. Tip early, then tweak the later games as news breaks.",
    howItWorksScoringTitle: "Four, three, two — or nothing",
    howItWorksScoringIntro:
      "Points land after the final whistle. Closer is better, and a perfect score is worth celebrating.",
    howItWorksExactTitle: "Exact score",
    howItWorksExactPts: "4 points",
    howItWorksExactBody: "You called it. Predicted 2–1, finished 2–1.",
    howItWorksDiffTitle: "Goal difference",
    howItWorksDiffPts: "3 points",
    howItWorksDiffBody:
      "Right margin, wrong score. A 3–1 tip for a 2–0 result still scores, and 1–1 for 2–2 does too.",
    howItWorksTendTitle: "Tendency",
    howItWorksTendPts: "2 points",
    howItWorksTendBody: "You got the winner (or the draw) but missed the margin.",
    howItWorksNoneTitle: "Miss",
    howItWorksNonePts: "0 points",
    howItWorksNoneBody: "Wrong winner. Shake it off — nine more tips come next week.",
    howItWorksLeaguesTitle: "Eighteen in a league. Climb the pyramid.",
    howItWorksLeaguesBody:
      "You compete in a Tippliga of 18 players. There is one 1. Tippliga, two second leagues (A and B), and four third leagues (A–D). Finish high and you go up. Finish low and you drop.",
    howItWorksJoinTitle: "You take a bot's place",
    howItWorksJoinBody:
      "Leagues start filled with bots. When you join, you replace a bot in the highest league that still has one. No waiting list, no empty table — you are in the fight from day one.",
    howItWorksRoundsTitle: "Seven rounds, one season",
    howItWorksRoundsBody:
      "The 34 Spieltage are split into seven rounds. After every round except the last, the table is settled, promotion and relegation happen, and everyone's points reset to zero. The final round decides the season — those standings are kept in History.",
    howItWorksRound1: "Round 1: matchdays 1–4",
    howItWorksRound2: "Round 2: matchdays 5–9",
    howItWorksRound3: "Round 3: matchdays 10–14",
    howItWorksRound4: "Round 4: matchdays 15–19",
    howItWorksRound5: "Round 5: matchdays 20–24",
    howItWorksRound6: "Round 6: matchdays 25–29",
    howItWorksRound7: "Round 7: matchdays 30–34",
    howItWorksPromoTitle: "Promotion and relegation",
    howItWorksPromoBody:
      "Top 3 in a league move up. Bottom 3 go down — except in the 1. Tippliga, where the bottom 6 are relegated so the two second leagues can send three each. The last round of the season skips this: you finish where you finish.",
    howItWorksHistoryTitle: "Past seasons stay on the board",
    howItWorksHistoryBody:
      "When a season ends, final tables are frozen. You can browse old campaigns, then opt in to the new one and take another bot's seat.",
    howItWorksCloseTitle: "The bots are already playing. Don't let them keep your spot.",
    howItWorksCloseBody:
      "Registration is free. Pick your scores, climb the Tippligen, and see if you really know the Bundesliga.",
  },
  de: {
    appTitle: "Bundestippliga",
    menu: "Menü",
    home: "Start",
    matches: "Spiele",
    predictions: "Tipps",
    teams: "Teams",
    leaderboard: "Rangliste",
    profile: "Profil",
    admin: "Admin",
    logout: "Abmelden",
    allLeagues: "Alle Ligen",
    more: "Mehr",
    username: "Benutzername",
    landingWelcomeTitle: "Willkommen bei Bundestippliga",
    landingWelcomeBody:
      "Tippe jeden Spieltag, steige in den Ligen auf und messe dich mit Freunden in der Bundesliga-Tippliga.",
    landingHowItWorks: "So funktioniert es",
    landingHowItWorksItem1: "Tippe Ergebnisse für jeden Spieltag.",
    landingHowItWorksItem2:
      "Sammle Punkte für richtige Resultate und exakte Tipps.",
    landingHowItWorksItem3: "Steige jede Runde in den Ligen auf.",
    loginTitle: "Anmelden",
    loginButton: "Anmelden",
    loginUsernameLabel: "Benutzername oder E-Mail",
    loginPasswordLabel: "Passwort",
    loginLoading: "Anmeldung läuft...",
    loginNoAccount: "Kein Konto? Registrieren",
    registerTitle: "Registrieren",
    registerCardTitle: "Konto erstellen",
    registerUsernameLabel: "Benutzername",
    registerEmailLabel: "E-Mail",
    registerPasswordLabel: "Passwort",
    registerConfirmPasswordLabel: "Passwort bestätigen",
    registerButton: "Registrieren",
    registerLoading: "Konto wird erstellt...",
    registerHaveAccount: "Schon ein Konto? Anmelden",
    preferredLanguage: "Bevorzugte Sprache",
    english: "Englisch",
    german: "Deutsch",
    profileTitle: "Profil",
    playerInformation: "Spielerinfo",
    displayName: "Anzeigename",
    email: "E-Mail",
    totalPoints: "Gesamtpunkte",
    points: "Punkte",
    statistics: "Statistik",
    accountActions: "Kontoaktionen",
    languagePreferences: "Spracheinstellungen",
    saveLanguage: "Sprache speichern",
    savingLanguage: "Sprache wird gespeichert...",
    languageSaved: "Sprache gespeichert.",
    welcomeUser: "Willkommen",
    homeIntro: "Tippe Bundesliga-Spiele und miss dich mit anderen Spielern!",
    quickStats: "Schnellstatistik",
    currentStatus: "Aktueller Status",
    seasonStatus: "Saisonstatus",
    nextMatchday: "Nächster Spieltag",
    matchdayInfoLoading: "Spieltag-Info wird geladen",
    active: "Aktiv",
    completed: "Abgeschlossen",
    upcoming: "Bevorstehend",
    viewUpcomingMatches: "Kommende Spiele anzeigen",
    seeLeaderboard: "Rangliste ansehen",
    teamInformation: "Team-Informationen",
    makeYourPicks: "Tipps abgeben",
    matchesPageTitle: "Bundesliga Spiele",
    matchdaysSegment: "Spieltage",
    upcomingSegment: "Kommende",
    allMatchesSegment: "Alle Spiele",
    loadingMatches: "Spiele werden geladen...",
    joinNewSeason: "Neuer Saison beitreten",
    joinNewSeasonBody:
      "Nimm den Platz eines Bots in der höchsten Liga, die noch Bots hat. Dein Ergebnis der letzten Saison bleibt in der Historie.",
    joinSeasonButton: "Saison beitreten",
    joiningSeason: "Beitritt läuft...",
    joinSeasonSuccess: "Du bist dabei! Zugewiesen zu",
    joinSeasonError: "Beitritt fehlgeschlagen. Bitte nochmal versuchen.",
    alreadyInSeason: "Du bist dieser Saison bereits beigetreten.",
    matchdaysCardTitle: "Bundesliga Spieltage",
    matchdaysCardIntro:
      "Klicke auf einen Spieltag, um Tipps für alle Spiele abzugeben:",
    matchdayLabel: "Spieltag",
    sampleDataNote:
      "Hinweis: Beispieldaten werden angezeigt. Die API ist aktuell nicht verfügbar.",
    matchInfo: "Spielinfo",
    seasonLabel: "Saison",
    yourPrediction: "Dein Tipp",
    score: "Ergebnis",
    makePredictionsForMatchday: "Tipps für Spieltag",
    noUpcomingMatches: "Keine kommenden Spiele gefunden.",
    noMatches: "Keine Spiele gefunden.",
    predictionNone: "Kein Tipp",
    predictionPerfect: "Perfekt!",
    predictionCorrect: "Richtiges Ergebnis",
    predictionWrong: "Falsch",
    predictionPredicted: "Getippt",
    invalidDate: "Ungültiges Datum",
    errorTitle: "Fehler",
    successTitle: "Erfolg",
    okButton: "OK",
    passwordsDoNotMatch: "Passwörter stimmen nicht überein",
    leaderboardTitle: "Rangliste",
    myLeague: "Meine Liga",
    overall: "Gesamt",
    overallRankings: "Gesamtrangliste",
    loadingLeaderboard: "Rangliste wird geladen...",
    youTag: " (Du)",
    predictionsCount: "Tipps",
    exactScoresLabel: "exakte Tipps",
    tierLabel: "Stufe",
    divisionLabel: "Division",
    playersLabel: "Spieler",
    yourPosition: "Deine Position",
    loadingLeagueStandings: "Ligatabelle wird geladen...",
    leagueStandingsTitle: "Ligatabelle",
    notInLeague: "Du bist derzeit in keiner Liga.",
    joinLeague: "Einer Liga beitreten",
    promotionRelegation: "Aufstieg & Abstieg",
    promotionZone: "Aufstiegszone",
    promotionZoneDesc: "Top 3 Spieler steigen in eine höhere Stufe auf",
    safeZone: "Sicherheitszone",
    safeZoneDesc: "Bleibt in der aktuellen Stufe",
    relegationZone: "Abstiegszone",
    relegationZoneDesc: "Die letzten 3 Spieler steigen ab",
    exactShort: "exakt",
    goalDiffShort: "Tor diff",
    tendencyShort: "Tendenz",
    allLeaguesTitle: "Alle Ligen",
    loadingLeagues: "Ligen werden geladen...",
    errorLoadingLeagues: "Fehler beim Laden der Ligen. Bitte nochmal versuchen.",
    browseAllLeagues: "Alle Ligen ansehen",
    exploreAllLeagues: "Erkunde",
    leaguesInSystem: "Ligen im System",
    roundLabel: "Runde",
    matchdaysLabel: "Spieltage",
    currentLabel: "Aktuell",
    membersLabel: "Mitglieder",
    allPlayersLabel: "Alle",
    noPlayersInLeague: "Noch keine Spieler in dieser Liga.",
    noLeaguesFound: "Keine Ligen gefunden.",
    collapseAll: "Alle einklappen",
    expandAll: "Alle ausklappen",
    matchdayTitle: "Spieltag",
    loadingMatchday: "Spieltag wird geladen...",
    loadingMatchdayData: "Spieltag-Daten werden geladen...",
    errorLoadingMatchday: "Spieltag-Daten konnten nicht geladen werden.",
    noDataReceived: "Keine Daten erhalten",
    makePredictions: "Tipps abgeben",
    viewResults: "Ergebnisse ansehen",
    matchdayStatistics: "Spieltag-Statistik",
    predictorsLabel: "Tippende",
    finishedLabel: "Beendet",
    finalScore: "Endstand",
    predictionLocked: "Tipp gesperrt",
    noPredictionMade: "Kein Tipp abgegeben",
    yourPredictionResult: "Dein Tipp & Ergebnis",
    predictedLabel: "Getippt",
    saveAllPredictions: "Alle Tipps speichern",
    saving: "Speichern...",
    predictionsSaved: "Tipps erfolgreich gespeichert!",
    enterAtLeastOnePrediction: "Bitte mindestens einen Tipp eingeben",
    failedToSavePredictions: "Tipps konnten nicht gespeichert werden",
    exactScoreLabel: "Exaktes Ergebnis",
    goalDifferenceLabel: "Tor-Differenz",
    tendencyLabel: "Tendenz",
    noPointsLabel: "Keine Punkte",
    adminDashboardTitle: "Admin Dashboard",
    manualCronExecution: "Manuelle Cron-Ausführung",
    manualCronDescription:
      "Geplante Aufgaben manuell starten. Einige Jobs können mehrere Minuten dauern.",
    cronJobStatus: "Cron-Status",
    refresh: "Aktualisieren",
    autoRefreshEvery30: "Auto-Refresh alle 30 Sekunden",
    manualTriggers: "Manuelle Auslöser",
    calculateScores: "Punkte berechnen",
    updateFixtures: "Spielplan aktualisieren",
    updateStandings: "Tabelle aktualisieren",
    processPromotionRelegation: "Auf-/Abstieg verarbeiten",
    endRoundProcessPR: "Runde beenden & Auf/Abstieg",
    stopAllCronJobs: "Alle Cron-Jobs stoppen",
    cronJobResult: "Cron-Ergebnis",
    rateLimitExceeded:
      "Rate-Limit erreicht. Bitte kurz warten und erneut versuchen.",
    errorPrefix: "Fehler",
    lastRun: "Letzter Lauf",
    nextRun: "Nächster Lauf",
    never: "Nie",
    systemInformation: "Systeminfo",
    environment: "Umgebung",
    lastStatusUpdate: "Letztes Status-Update",
    autoRefresh: "Auto-Refresh",
    every5Seconds: "Alle 5 Sekunden",
    cronDescriptionScore: "Punkte für abgeschlossene Spiele berechnen",
    cronDescriptionFixture: "Spielplan von externer API aktualisieren",
    cronDescriptionStandings: "Ligatabelle und Rankings aktualisieren",
    cronDescriptionPromotion: "Auf- und Abstieg verarbeiten",
    cronDescriptionRoundTransition: "Runde beenden und Auf/Abstieg verarbeiten",
    predictionsTitle: "Meine Tipps",
    loadingPredictions: "Tipps werden geladen...",
    errorPredictions: "Fehler beim Laden der Tipps. Bitte nochmal versuchen.",
    actualResult: "Tatsächliches Ergebnis",
    status: "Status",
    pointsEarned: "Punkte erhalten",
    matchDate: "Spieldatum",
    predictionMade: "Tipp abgegeben",
    noPredictions: "Keine Tipps gefunden. Fang an zu tippen!",
    viewMatches: "Spiele ansehen",
    pending: "Ausstehend",
    teamsTitle: "Teams",
    searchTeamsPlaceholder: "Teams suchen...",
    loadingTeams: "Teams werden geladen...",
    errorTeams: "Fehler beim Laden der Teams. Bitte nochmal versuchen.",
    shortName: "Kurzname",
    leagueLabel: "Liga",
    noTeams: "Keine Teams gefunden.",
    historyTitle: "Historie",
    loadingHistory: "Historie wird geladen...",
    errorLoadingHistory: "Fehler beim Laden der Historie. Bitte nochmal versuchen.",
    pastSeasons: "Vergangene Saisons",
    noPastSeasons: "Noch keine abgeschlossenen Saisons.",
    roundsPlayed: "Runden gespielt",
    finalStandings: "Endstand",
    finalRound: "Letzte Runde",
    howItWorksNav: "So funktioniert's",
    howItWorksTitle: "Tippen. Aufsteigen. Den Spieltag gewinnen.",
    howItWorksLead:
      "Bundestippliga ist die kostenlose Bundesliga-Tippliga. Tippe jedes Spiel, sammle Punkte und kämpfe dich durch die Tippligen — wie in der echten Tabelle, nur dass du die Mannschaft bist.",
    howItWorksCtaRegister: "Kostenlos Konto erstellen",
    howItWorksCtaLogin: "Ich habe schon ein Konto",
    howItWorksPlayTitle: "Tippe jeden Spieltag",
    howItWorksPlayBody:
      "Jeder Spieltag hat neun Bundesliga-Spiele. Trag das Ergebnis ein, das du erwartest, und speichere. Leere Felder zählen als 0:0 — also lieber bewusst tippen. Bis zur Sperre kannst du jeden Tipp noch ändern.",
    howItWorksDeadlineTitle: "Sperre eine Minute vor Anpfiff",
    howItWorksDeadlineBody:
      "Jedes Spiel hat seine eigene Deadline: eine Minute vor Anpfiff. Das Freitagsspiel kann schon zu sein, während die Sonntagsspiele noch offen sind. Tippe früh, und schärfe die späteren Spiele nach, wenn News kommen.",
    howItWorksScoringTitle: "Vier, drei, zwei — oder nichts",
    howItWorksScoringIntro:
      "Punkte gibt es nach dem Abpfiff. Je näher am Ergebnis, desto besser. Der perfekte Tipp ist Gold wert.",
    howItWorksExactTitle: "Genaues Ergebnis",
    howItWorksExactPts: "4 Punkte",
    howItWorksExactBody: "Volltreffer. Getippt 2:1, gespielt 2:1.",
    howItWorksDiffTitle: "Tordifferenz",
    howItWorksDiffPts: "3 Punkte",
    howItWorksDiffBody:
      "Richtige Differenz, falscher Spielstand. Ein 3:1-Tipp bei 2:0 zählt, genauso 1:1 bei 2:2.",
    howItWorksTendTitle: "Tendenz",
    howItWorksTendPts: "2 Punkte",
    howItWorksTendBody: "Sieger (oder Unentschieden) stimmt, die Tore nicht.",
    howItWorksNoneTitle: "Daneben",
    howItWorksNonePts: "0 Punkte",
    howItWorksNoneBody: "Falscher Sieger. Abhaken — nächste Woche kommen neun neue Tipps.",
    howItWorksLeaguesTitle: "Achtzehn in einer Liga. Rauf durch die Pyramide.",
    howItWorksLeaguesBody:
      "Du spielst in einer Tippliga mit 18 Spielern. Es gibt eine 1. Tippliga, zwei zweite Ligen (A und B) und vier dritte Ligen (A–D). Oben bleibst du, oder du steigst auf. Unten geht's runter.",
    howItWorksJoinTitle: "Du nimmst einem Bot den Platz",
    howItWorksJoinBody:
      "Die Ligen starten mit Bots. Wenn du beitrittst, ersetzt du einen Bot in der höchsten Liga, die noch einen hat. Keine Warteliste, kein leeres Feld — du bist sofort im Kampf.",
    howItWorksRoundsTitle: "Sieben Runden, eine Saison",
    howItWorksRoundsBody:
      "Die 34 Spieltage sind in sieben Runden geteilt. Nach jeder Runde außer der letzten wird abgerechnet, auf- und abgestiegen, und alle Punkte fallen auf null. Die letzte Runde entscheidet die Saison — der Endstand bleibt in der Historie.",
    howItWorksRound1: "Runde 1: Spieltage 1–4",
    howItWorksRound2: "Runde 2: Spieltage 5–9",
    howItWorksRound3: "Runde 3: Spieltage 10–14",
    howItWorksRound4: "Runde 4: Spieltage 15–19",
    howItWorksRound5: "Runde 5: Spieltage 20–24",
    howItWorksRound6: "Runde 6: Spieltage 25–29",
    howItWorksRound7: "Runde 7: Spieltage 30–34",
    howItWorksPromoTitle: "Aufstieg und Abstieg",
    howItWorksPromoBody:
      "Die Top 3 einer Liga steigen auf. Die letzten 3 steigen ab — außer in der 1. Tippliga, aus der die letzten 6 absteigen, damit beide zweiten Ligen je drei hochschicken können. In der letzten Runde entfällt das: Du bleibst, wo du landest.",
    howItWorksHistoryTitle: "Vergangene Saisons bleiben stehen",
    howItWorksHistoryBody:
      "Am Saisonende wird die Tabelle eingefroren. Du kannst alte Spielzeiten durchstöbern und dich danach für die neue Saison anmelden — wieder auf einem Bot-Platz.",
    howItWorksCloseTitle: "Die Bots tippen schon. Lass ihnen nicht deinen Platz.",
    howItWorksCloseBody:
      "Die Anmeldung ist kostenlos. Tippe die Spiele, steig in den Tippligen auf, und zeig, ob du die Bundesliga wirklich kannst.",
  },
} as const;

export const DEFAULT_LANGUAGE: Language = "de";

export const normalizeLanguage = (value?: string): Language =>
  value === "en" ? "en" : "de";

export const getStoredLanguage = (): Language => {
  if (typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }
  return normalizeLanguage(
    window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || DEFAULT_LANGUAGE
  );
};

export const setStoredLanguage = (language: Language): void => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
};

export const useI18n = () => {
  const { player } = useAuthStore();
  const language = normalizeLanguage(player?.preferredLanguage || getStoredLanguage());

  const t = (key: keyof typeof translations.en): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  return { language, t };
};
